import {BaseElement, BaseElementAsync, BASIS, MsgLabel, OSkinableInit} from "/@back@/commons/basis.js";
import {ITaskUiEnv} from "/@back@/wsp/views/itemMain.js";
import {IReg, REG} from "/@lib@/commons/registry.js";
import {IView, VIEWS} from "/@lib@/commons/views.js";
import {JSX} from "/@lib@/commons/xml/dom.js";
import {DOMSH} from "/@lib@/commons/xml/domsh.js";
import {IWspUiEnv, JWspUriChangeMsg, WSP} from "/@lib@/wsp/wsp.js";
import {ItemEditInDialogInfoBroker, ItemXmlEd} from "/@back@/wsp/views/item/itemXmlEd.js";
import {JActTouchedContent, JSrcFields, SRC, srcId, srcRef, srcUri} from "/@lib@/wsp/src.js";
import {ItemDynGen, OItemDynGenInit} from "/@back@/wsp/views/item/itemDynGen.js";
import {IInfo, IInfoConsumer, IInfoProducer} from "/@lib@/commons/infos.js";
import {InfoFocusItem, IShortDescCtx} from "/@lib@/wsp/item.js";
import {ItemViewerSingle} from "/@back@/wsp/views/itemViewerSingle.js";
import {EItemTypeFamily} from "/@lib@/wsp/wspMetaUi.js";
import {DynGen} from "/@back@/wsp/views/dynGen.js";
import {ButtonActions, OButtonActionsInit} from "/@back@/commons/widgets/buttons.js";
import {SrcExecTransition} from "/@back@/wsp/actions/lcTaskActions.js";
import {LIFECYCLE} from "/@lib@/wsp/lcTask.js";
import {EWspChangesEvts, WspsLivePlace} from "/@lib@/wsp/wspsLive.js";
import {JLastDatas} from "/@lib@/commons/lastDatas.js";


/** Items de contenu affichés
 * 		Remarque : ce message ne concerne pas l'édition de la task courante et sont donc à envoyer sur l'infoBroker courant, et non pas sur taskEditBroker
 * */
export class InfoReqTaskContentItems implements IInfo {
	/** null pour signifier aucun item content courant */
	entries: {
		entrySgn: string,
		srcUri?: srcUri;
		/** shortDesc de l'item courant si srcUri non null.*/
		shortDesc?: JSrcFields;
	}[]
	infoHolders?: IInfoProducer[];
}

/**
 * Message annonçant l'évolution d'affichage d'un taskContent
 * 	Remarque : ce message ne concerne pas l'édition de la task courante et sont donc à envoyer sur l'infoBroker courant, et non pas sur taskEditBroker
 */
export class InfoTaskCurrentItemsChange implements IInfo {
	handled: boolean;

	constructor(public srcRef?: srcRef, public shortDesc?: JSrcFields, public lastDatas?: JLastDatas) {
	}

	infoHolders?: IInfoProducer[];
}

/**
 * Edition d'un item lié à une tâche.
 *
 * Config attributes:
 */
export interface TaskCtEdit extends BaseElementAsync {
	initialize(init: OTaskCtEditInit): this;
}

export interface OTaskCtInit {
	/** todo Sélection de l'item à éditer. Par défaut : le 1er ! */
	//selectItem?: ()=>srcRef

	/** Si true, modifie dynamiquement le content en cours d'édition en fonction de l'info "focusItem"**/
	handleFocusItem: boolean
}

export interface OTaskCtEditInit extends OTaskCtInit, OSkinableInit {
	reg?: IReg<ITaskUiEnv>

	/** Editor à utiliser (mode 'editor'). */
	editorKey?: string

	/** Permission spécifique à cet éditeur (mode 'editor'). */
	writePerms?: string | string[]

	/** editor : juste l'éditeur spécifié si existant
	 *  mainView : affiche la vue "mainView" (en mode ':hideWspStruct:')
	 * Si non spécifié : 'editor' si disponible, ou 'mainView' à défaut */
	mode?: 'editor' | 'mainView'
}

export class TaskCtEdit extends BaseElementAsync implements IView, IInfoConsumer {
	reg: IReg<ITaskUiEnv>;

	config: OTaskCtEditInit;

	editor: ItemXmlEd;

	protected _currentItemToEdit: JSrcFields;

	/** Fallback si pas d'éditeur. */
	itemViewer: ItemViewerSingle;

	protected _shown: boolean;

	async _initialize(init: OTaskCtEditInit) {
		this.reg = this.findReg(init);
		this.config = init;
		const sr = this.attachShadow(DOMSH.SHADOWDOM_INIT);
		this._initAndInstallSkin(this.localName, init);
		const itemToEdit = findItemToEdit(this.reg, init);
		this.reg.env.infoBroker?.addConsumer(this);
		if (!itemToEdit) {
			sr.appendChild(new MsgLabel().initialize({label: "Aucun item n'est lié à cette tâche"}));
		} else {
			const srcId = itemToEdit.srcId;
			await this.initEditor(srcId);
		}
	}

	/**
	 * Initialise ou remplace l'éditeur
	 * @param srcId
	 */
	protected async initEditor(srcId: srcId, shortDesc?: JSrcFields) {
		this.clearContent();
		const wspReg = WSP.findWspReg(this.reg);
		const wsp = wspReg.env.wsp;
		if (!shortDesc) shortDesc = await WSP.fetchShortDesc(wsp, this, srcId);
		this._currentItemToEdit = shortDesc;
		const itemType = wsp.wspMetaUi.getItemType(shortDesc.itModel);
		if (this.config.mode !== 'mainView' && itemType.getFamily() === EItemTypeFamily.xml && itemType.getEditor(this.config.editorKey)) {
			this.editor = this.shadowRoot.appendChild(new ItemXmlEd().initialize({
				reg: wspReg,
				srcRef: srcId,
				writePerms: this.config.writePerms,
				editorKey: this.config.editorKey,
				initItemReg: (reg) => {
					reg.env.infoBroker = new ItemEditInDialogInfoBroker(wspReg);
				}
			}));
			if (this._shown) VIEWS.onViewShown(this.editor);
		} else {
			//Cet item n'est pas un item XML ou ne possède pas d'éditeur this.config.editorKey
			//On affiche la mainView à la place.
			const subReg = REG.createSubReg<IWspUiEnv>(wspReg);
			subReg.env.infoBroker = new ItemEditInDialogInfoBroker(wspReg);
			this.itemViewer = new ItemViewerSingle({ mainViewCode: ":hideWspStruct:" });
			const root = this.shadowRoot.appendChild(<div/>);
			await this.itemViewer.initViewer(subReg, root, null, srcId);
			if (this._shown) VIEWS.onViewShown(this.itemViewer);
		}
	}

	protected clearContent() {
		if (this.editor) {
			if (this._shown) VIEWS.onViewHidden(this.editor, true);
			this.shadowRoot.removeChild(this.editor);
			this.editor = null;
		}
		if (this.itemViewer) {
			if (this._shown) VIEWS.onViewHidden(this.itemViewer, true);
			this.itemViewer.mainViewParent.remove();
			this.itemViewer = null;
		}
	}

	onInfo(info: IInfo): void {
		if (this.config.handleFocusItem) {
			if (info instanceof InfoFocusItem && !info.handled) {
				info.handled = true;
				this.initEditor(info.srcRef, info.shortDesc);
				this.reg.env.infoBroker?.dispatchInfo(new InfoTaskCurrentItemsChange(info.srcRef, info.shortDesc), this);
			}
		}
		if (info instanceof InfoReqTaskContentItems) {
			if (this.hidden) return;
			if (this._currentItemToEdit) {
				if (!info.entries)
					info.entries = [];
				info.entries.push({
					entrySgn: 'taskCtEdit',
					shortDesc: this._currentItemToEdit,
				})
			}
		}
	}

	onViewShown() {
		this._shown = true;
		if (this.editor) VIEWS.onViewShown(this.editor);
		if (this.itemViewer) VIEWS.onViewShown(this.itemViewer);
		if (this._currentItemToEdit) this.reg.env.infoBroker?.dispatchInfo(new InfoTaskCurrentItemsChange(this._currentItemToEdit.srcUri, this._currentItemToEdit), this);
	}

	onViewHidden(closed?: boolean) {
		this._shown = false;
		if (this.editor) VIEWS.onViewHidden(this.editor, closed);
		if (this.itemViewer) VIEWS.onViewHidden(this.itemViewer, closed);
		this.reg.env.infoBroker?.dispatchInfo(new InfoTaskCurrentItemsChange(), this);
	}


}

REG.reg.registerSkin("wsp-task-ct-edit", 1, /* language=CSS */ `
	:host,
	div {
		flex: 1;
		display: flex;
		min-height: 0;
		min-width: 0;
		flex-direction: column;
	}

	wsp-item-xmled {
		flex: 1;
	}
`);

customElements.define("wsp-task-ct-edit", TaskCtEdit);

/**
 * DynGen d'un item lié à une tâche.
 *
 * Config attributes:
 */

export interface TaskCtDynGen extends BaseElement {
	initialize(init: OTaskCtDynGenInit): this;
}

export interface OTaskCtDynGenInit extends OTaskCtInit, OItemDynGenInit {
	reg?: IReg<ITaskUiEnv>
}

export class TaskCtDynGen extends BaseElement implements IView, IInfoConsumer {
	reg: IReg<ITaskUiEnv>;

	config: OTaskCtDynGenInit;

	itemDynGen: ItemDynGen;

	srcId: srcId;

	/** Fallback si pas de dynGen. */
	itemViewer: ItemViewerSingle;

	protected _shown: boolean;

	_initialize(init: OTaskCtDynGenInit) {
		this.reg = this.findReg(init);
		this.config = init;
		const sr = this.attachShadow(DOMSH.SHADOWDOM_INIT);
		this._initAndInstallSkin(this.localName, init);
		const itemToShow = findItemToEdit(this.reg, init);
		this.reg.env.infoBroker?.addConsumer(this);
		if (!itemToShow)
			this.appendChild(new MsgLabel().initialize({label: "Aucun item n'est lié à cette tâche"}));
		else
			this.initDynGen(itemToShow.srcId);
	}

	/**
	 * Initialise ou remplace la dynGen
	 * @param srcId
	 */
	private initDynGen(srcId: srcId) {
		this.clearContent();
		this.srcId = srcId;
		const wspReg = WSP.findWspReg(this.reg);
		const dynGenInit: OItemDynGenInit = BASIS.newInit(this.config, wspReg);
		dynGenInit.srcRef = srcId;
		if (!dynGenInit.toolbar) dynGenInit.toolbar = false;
		this.itemDynGen = this.shadowRoot.appendChild(new ItemDynGen().initialize(dynGenInit));
		if (this._shown) VIEWS.onViewShown(this.itemDynGen);
		this.itemDynGen.view.onError.add(this.onGenError);
	}

	protected clearContent() {
		if (this.itemDynGen) {
			VIEWS.onViewHidden(this.itemDynGen, true);
			this.shadowRoot.removeChild(this.itemDynGen);
			this.itemDynGen.view.onError.delete(this.onGenError);
			this.itemDynGen = null;
		}
		if (this.itemViewer) {
			if (this._shown) VIEWS.onViewHidden(this.itemViewer, true);
			this.itemViewer.mainViewParent.remove();
			this.itemViewer = null;
		}
	}

	onInfo(info: IInfo): void {
		if (this.config.handleFocusItem) {
			if (info instanceof InfoFocusItem && !info.handled) {
				info.handled = true;
				this.initDynGen(info.srcRef);
				this.reg.env.infoBroker?.dispatchInfo(new InfoTaskCurrentItemsChange(info.srcRef), this);
			}
		}
		if (info instanceof InfoReqTaskContentItems) {
			if (this.hidden || !this._shown) return;
			if (this.srcId) {
				if (!info.entries)
					info.entries = [];
				info.entries.push({
					entrySgn: 'taskCtDynGen',
					srcUri: this.srcId,
				})
			}
		}

	}

	onViewShown() {
		this._shown = true;
		if (this.itemDynGen) VIEWS.onViewShown(this.itemDynGen);
		if (this.itemViewer) VIEWS.onViewShown(this.itemViewer);
		if (this.srcId) this.reg.env.infoBroker?.dispatchInfo(new InfoTaskCurrentItemsChange(this.srcId), this);
	}

	onViewHidden(closed?: boolean) {
		this._shown = false;
		if (this.itemDynGen) VIEWS.onViewHidden(this.itemDynGen, closed);
		if (this.itemViewer) VIEWS.onViewHidden(this.itemViewer, closed);
		this.reg.env.infoBroker?.dispatchInfo(new InfoTaskCurrentItemsChange(), this);
	}

	onGenError(this: void, err: 'empty' | 'message', msg: Element, dynGen: DynGen) {
		//Erreur à l'affichage du dyGen, on bascule sur la mainView.
		const me = DOMSH.findHost<TaskCtDynGen>(DOMSH.findHost<ItemDynGen>(dynGen));
		me.clearContent();
		me.itemViewer = new ItemViewerSingle();
		const root = me.shadowRoot.appendChild(<div/>);
		me.itemViewer.initViewer(me.reg, root, null, me.srcId);
		if (me._shown) VIEWS.onViewShown(me.itemViewer);
	}
}

REG.reg.registerSkin("wsp-task-ct-dyngen", 1, /* language=CSS */ `
	:host,
	div {
		flex: 1;
		display: flex;
		min-height: 0;
		min-width: 0;
		flex-direction: column;
	}

`);

customElements.define("wsp-task-ct-dyngen", TaskCtDynGen);


function findItemToEdit(reg: IReg<ITaskUiEnv>, config: OTaskCtInit): JActTouchedContent {
	const taskDesc = reg.env.longDesc;
	const cts = taskDesc.actCts;
	if (cts) return cts[0];
}


/**
 * LC sur l'item root associé à la task
 */
export interface OTaskCtLcInit extends OTaskCtInit, OButtonActionsInit<IShortDescCtx> {
	reg?: IReg<ITaskUiEnv>
	/** true par défaut */
	editable?: boolean
	/** Si true, accepte que le champ soit en erreur à l'enregistrement.
	 * false par défaut */
	permissive?: boolean
	writePerms?: string | string[]
}

export class TaskCtLc extends ButtonActions<IShortDescCtx> implements IShortDescCtx, IView, IInfoConsumer {
	reg: IReg<ITaskUiEnv>;

	config: OTaskCtLcInit;

	srcId: srcId;

	srcLongDesc: JSrcFields;

	selectedTrans: SrcExecTransition<IShortDescCtx>;

	protected onWspUriChange: (msg: JWspUriChangeMsg, from: WspsLivePlace | 'local' | 'server') => void;

	get fieldName() {return 'lcSt' as 'lcSt'}

	get value(): string {return this.selectedTrans ? this.selectedTrans.trans.targetState : (this.srcLongDesc ? this.srcLongDesc.lcSt : '')}

	get label() {
		return this.reg.env.wsp.wspMetaUi.getLcStateOrUnknown(this.value).name;
	}

	get icon() {
		return this.reg.env.wsp.wspMetaUi.getLcStateOrUnknown(this.value).iconUrl;
	}

	get shortDescs() {return [this.srcLongDesc]}

	get emitter() {return this}

	protected _initialize(init: OTaskCtLcInit): void {
		this.reg = this.findReg(init);
		this.config = init;
		init.skinOver = this.localName;
		init.groupOrder = this.reg.env.wsp.wspMetaUi.getLcTransitionsGroupOrder();
		this.onWspUriChange = this._onWspUriChange.bind(this);
		this.reg.env.infoBroker?.addConsumer(this);
		this.reg.env.place.eventsMgr.on("wspUriChange", this.onWspUriChange);
		super._initialize(init);
		const itemToShow = findItemToEdit(this.reg, this.config);
		this.onRefreshTask(itemToShow.srcId);
	}

	/**
	 * Initialise ou remplace le lc
	 * @param srcId
	 */
	private async onRefreshTask(srcId: srcId, longDesc?: JSrcFields) {
		this.innerHTML = "";
		if (!srcId)
			this.appendChild(new MsgLabel().initialize({label: "Aucun item n'est lié à cette tâche"}));
		else {
			this.srcId = srcId;
			this.srcLongDesc = longDesc || await this.reg.env.wsp.fetchLongDesc(this.srcId);
			this.actions = LIFECYCLE.listAllTransitionActions(this.reg.env.wsp.wspMetaUi);
			this.refresh();
		}
	}

	protected _refresh() {
		super._refresh();
		this.disabled = this.config.editable === false || (this.config.writePerms && !this.reg.hasPerm(this.config.writePerms));
	}

	onViewHidden(closed?: boolean) {
		if (closed) {
			this.reg.env.place.eventsMgr.removeListener("wspUriChange", this.onWspUriChange);
		}
	}

	onInfo(info: IInfo): void {
		if (this.config.handleFocusItem) {
			if (info instanceof InfoFocusItem && !info.handled) {
				info.handled = true;
				this.reg.env.infoBroker?.dispatchInfo(new InfoTaskCurrentItemsChange(info.shortDesc.srcId), this);
				this.onRefreshTask(info.shortDesc.srcId);//longDesc non fourni ici
			}
		}
		if (info instanceof InfoReqTaskContentItems) {
			if (this.hidden) return;
			if (this.srcLongDesc || this.srcId) {
				if (!info.entries)
					info.entries = [];
				info.entries.push({
					entrySgn: 'taskCtLc',
					shortDesc: this.srcLongDesc,
					srcUri: this.srcId,
				})
			}
		}
	}

	protected _onWspUriChange(msg: JWspUriChangeMsg, from: WspsLivePlace | 'local' | 'server') {
		if (this.srcLongDesc) {
			if (msg.type === EWspChangesEvts.lcSt && SRC.srcRef(this.srcLongDesc) === SRC.srcRef(msg)) {
				this.onRefreshTask(msg.srcId);//longdesc pas disponible
			}
		}
	}

}

REG.reg.registerSkin("wsp-task-ct-lc", 1, /* language=CSS */ `
	:host {
		color: var(--edit-color);
	}

	:host([disabled]) {
		opacity: unset;
	}
`);

customElements.define("wsp-task-ct-lc", TaskCtLc);